import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

// Scroll ile tetiklenen giriş animasyonu sarmalayıcısı
export const Reveal = ({ children, className = "", direction = "up", delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const dirClass =
    direction === "left" ? "reveal-left" : direction === "right" ? "reveal-right" : "";

  return (
    <div
      ref={ref}
      className={`reveal ${dirClass} ${visible ? "reveal-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// Sayı sayacı animasyonu
export const CountUp = ({ end, duration = 1800, suffix = "", prefix = "" }) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.round(eased * end));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {value.toLocaleString("tr-TR").replace(/\./g, "")}
      {suffix}
    </span>
  );
};

// Kurumsal bölüm etiketi
export const SectionLabel = ({ num, children, light = false }) => (
  <div className="flex items-center gap-3 mb-5">
    <span className="h-px w-8 fine-line" />
    <span
      className={`section-label text-[11px] font-semibold uppercase ${
        light ? "text-white/50" : "text-[#5b6b7d]"
      }`}
    >
      {children}
    </span>
  </div>
);

// Sayfa başlık bandosu (iç sayfalar) — premium
export const PageHero = ({ title, subtitle, crumb }) => {
  return (
    <section className="relative bg-[#050e1d] pt-40 pb-16 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.14]"
        style={{
          backgroundImage: "url('/images/hero-1.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050e1d]/95 via-[#081f3b]/85 to-[#0d2b4f]/60" />
      {/* İnce dikey çizgiler */}
      <div className="absolute inset-0 pointer-events-none hidden lg:block">
        <div className="max-w-7xl mx-auto h-full px-8 flex justify-between">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className="w-px h-full bg-white/[0.05]" />
          ))}
        </div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] font-semibold text-white/50">
            <Link to="/" className="hover:text-white transition-colors">
              Anasayfa
            </Link>
            <span className="text-[#1e9cd7]">/</span>
            <span className="text-white/80">{crumb || title}</span>
          </div>
          <h1 className="mt-5 text-3xl sm:text-4xl lg:text-[40px] font-bold text-white leading-tight tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-4 text-white/60 max-w-2xl text-[15px] sm:text-base leading-relaxed">
              {subtitle}
            </p>
          )}
          <span className="mt-8 block h-px w-20 fine-line" />
        </Reveal>
      </div>
    </section>
  );
};
